import type { PodBrand } from '@/types/pod'

/**
 * The Falmouth Training Solutions pod — branding layer (browser side).
 *
 * A SALES DEMO, NOT A CUSTOMER. Falmouth Training Solutions Ltd (Bridge House,
 * Tregoniggie, Falmouth, TR11 4SN) has not bought anything and has not signed a
 * DPA. Consequences, all deliberate and identical in kind to swsteel and ctc:
 *   - registry entry carries NO `billing` flag and NO `customerSince`
 *     (`billing: 'operator'` marks the operator's OWN products; absence of
 *     `customerSince` is what marks a demo)
 *   - `notifyPhone` is the OPERATOR'S mobile — an enquiry captured here is a
 *     demo artefact, not their lead, and must not reach them
 *   - no `publishDomain`, no `missedCall`: both point at a domain and a phone
 *     line the operator does not control
 *
 * SOURCE OF TRUTH. Every value below is from falmouthtrainingsolutions.co.uk,
 * read 15 Sep 2026 — the phone, email and address from the site header and
 * footer, the tagline from their own hero, the starters from the questions the
 * course range and the contact form's own dropdown show people arrive with.
 *
 * NO OPENING HOURS ANYWHERE. Unlike ctc, this business does not publish office
 * hours on its website. None are invented here and the corpus tells the
 * assistant to say so rather than guess. If they buy, ask them.
 *
 * WELCOME INTRO SPLITS MARITIME FROM WORKPLACE, DELIBERATELY. Two quite
 * different customers arrive at this site: a seafarer or superyacht crew member
 * chasing an STCW or MCA ticket, and a local employer wanting first aid or fire
 * training for staff. Opening with only one of them loses the other, and the
 * first useful act of the conversation is finding out which one is typing.
 *
 * STARTERS INCLUDE THE SISTER-COMPANY TRAP ON PURPOSE. "Do you do Day Skipper?"
 * is the question this pod most needs to get right: RYA sailing, powerboat,
 * shorebased theory and Diesel Engine Maintenance belong to Falmouth School of
 * Sailing and Falmouth Powerboat School, NOT to FTS. An assistant that answers
 * it as though FTS ran the course is wrong in a way the enquirer cannot detect.
 * Putting it on the front of the widget makes the correct behaviour the first
 * thing their team sees.
 *
 * PALETTE. Navy and a deep sea blue against near-white, reading off their site
 * chrome and logo. ESTIMATED FROM THE RENDERED PAGE, not a brand sheet — tune
 * once seen live beside their logo.
 *
 * builtByForedesk IS ON, as on swsteel and ctc: a pitch asset shown to a
 * prospect, not a business's own front of house, so the credit is wanted.
 */
export const ftsPod: PodBrand = {
  id: 'fts',
  brandName: 'Falmouth Training Solutions',
  productName: 'Falmouth Training Solutions',
  assistantName: 'Tamsin',
  tagline: 'Maritime and workplace training in Falmouth',
  roleLabel: 'training assistant',
  welcomeIntro:
    "Tell me what the training is for and I'll point you at the right course. Going to sea — STCW, MCA tickets, engineering, superyacht crew, apprenticeships? Or training for a workplace — first aid, fire marshal, food safety? Either way, say whether it's a first qualification or an update to one you already hold, and I'll take it from there and get it to the office.",
  starters: [
    'What do I need to work on a yacht?',
    'Do I need to update my STCW?',
    'First aid training for our staff',
    'Do you do Day Skipper?',
  ],
  inputPrompt: 'Ask Tamsin about courses',
  footerNote:
    'Tamsin answers from Falmouth Training Solutions\u2019 own information \u2014 anything else goes straight to the office. Not first aid, medical or emergency advice; call 999 in an emergency, and ask for the Coastguard if it is at sea.',
  builtByForedesk: true,
  theme: {
    // Navy and sea blue on near-white, sampled from the rendered site, 15 Sep 2026.
    '--color-brand-primary': '16 46 84', // navy #102E54
    '--color-brand-primary-hover': '11 34 64', // #0B2240
    '--color-brand-accent': '0 122 166', // sea blue #007AA6
    '--color-surface-base': '249 250 251',
    '--color-surface-raised': '255 255 255',
    '--color-surface-subtle': '241 245 248',
    '--color-text-primary': '16 34 56',
    '--color-text-muted': '96 112 130',
    '--color-text-danger': '178 47 47',
    '--color-border-subtle': '224 231 237',
  },
  contact: {
    phone: '01326 211311',
    email: 'info@falmouthtrainingsolutions.co.uk',
  },
}
